/**
 * UserPicker — Combobox mit Direkteingabe aus den hinterlegten Mitarbeitern
 * (Migration 0057). Typeahead gegen GET /users?query=; Auswahl per Klick.
 *
 * Zwei Modi:
 *  - multiple (Default): Mehrfachauswahl als Chips (Assignee-Fan-out).
 *  - single: eine Auswahl, schliesst die Liste (Weiterreichen/Delegate).
 *
 * Styling folgt den Task-Modal-Feldern (border-border-strong, rounded-md,
 * Accent-Focus-Ring).
 */
import { useEffect, useRef, useState } from "react";
import { Search, X, Loader2, Check } from "lucide-react";
import { api } from "../api";
import { useT } from "../i18n";

export interface Person {
  name: string;
  email: string;
  department?: string | null;
}

export function UserPicker({
  selected,
  onChange,
  multiple = true,
  // Ohne eigenen Platzhalter kommt der Text aus dem Katalog (der Default kann
  // kein `t` aufrufen — Hooks laufen erst im Rumpf).
  placeholder,
  autoFocus = false,
}: {
  selected: Person[];
  onChange: (next: Person[]) => void;
  multiple?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const t = useT();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Person[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // Debounced Typeahead gegen /users.
  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const q = query.trim();
        const list = await api.get<Person[]>(
          `/users${q ? `?query=${encodeURIComponent(q)}` : ""}`
        );
        // Defensiv: liefert der Proxy statt JSON die SPA-index.html zurück
        // (Pfad nicht im API-Allowlist), ist `list` ein String → nicht mappen,
        // sonst crasht der Dropdown-Render ("o.map is not a function").
        if (!cancelled) setResults(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query]);

  // Klick ausserhalb schliesst die Dropdown-Liste.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const isSelected = (p: Person) =>
    selected.some((s) => s.email.toLowerCase() === p.email.toLowerCase());

  const toggle = (p: Person) => {
    if (multiple) {
      if (isSelected(p)) {
        onChange(selected.filter((s) => s.email.toLowerCase() !== p.email.toLowerCase()));
      } else {
        onChange([...selected, p]);
      }
      setQuery("");
    } else {
      onChange([p]);
      setQuery("");
      setOpen(false);
    }
  };

  const remove = (p: Person) =>
    onChange(selected.filter((s) => s.email.toLowerCase() !== p.email.toLowerCase()));

  return (
    <div ref={boxRef} className="relative">
      {/* Chips der aktuellen Auswahl (nur im Multi-Modus sichtbar sammelnd) */}
      {multiple && selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {selected.map((p) => (
            <span
              key={p.email}
              className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-brand-100 dark:bg-brand-700/30 text-brand-700 dark:text-brand-100 text-xs"
            >
              {p.name}
              <button
                type="button"
                onClick={() => remove(p)}
                aria-label={t.shared.personen.entfernen(p.name)}
                className="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-3 pointer-events-none" />
        <input
          type="text"
          value={query}
          autoFocus={autoFocus}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={
            !multiple && selected.length > 0
              ? selected[0].name
              : (placeholder ?? t.shared.personen.platzhalter)
          }
          className="w-full pl-8 pr-3 py-2 text-sm bg-surface border border-border-strong rounded-md focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
        />
      </div>

      {open && (
        <div className="absolute z-[60] mt-1 w-full max-h-60 overflow-y-auto rounded-md border border-border-strong bg-surface shadow-lg">
          {loading && (
            <div className="px-3 py-2 text-xs text-text-3 flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> {t.shared.personen.laedt}
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-text-3">
              {query.trim()
                ? t.shared.personen.keineTrefferFuer(query.trim())
                : t.shared.personen.keineTreffer}
            </div>
          )}
          {!loading &&
            results.map((p) => {
              const sel = isSelected(p);
              return (
                <button
                  key={p.email}
                  type="button"
                  onClick={() => toggle(p)}
                  className="w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-surface-2 focus:bg-surface-2 focus:outline-none"
                >
                  <span
                    className={
                      "w-4 h-4 rounded border flex items-center justify-center shrink-0 " +
                      (sel
                        ? "bg-brand-500 border-brand-500 text-white"
                        : "border-border-strong")
                    }
                  >
                    {sel && <Check className="w-3 h-3" />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-text truncate">{p.name}</span>
                    <span className="block text-[11px] text-text-3 truncate">
                      {p.email}
                      {p.department ? ` · ${p.department}` : ""}
                    </span>
                  </span>
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}

export default UserPicker;
